/**
 * hrc-product-card.js
 * v2.1 — Gallery Slider + Quick Add → Popup
 *
 * Quick Add butonuna basıldığında artık direkt sepete eklemek yerine
 * HrcQuickAddPopup.open(handle) çağırır.
 */

if (!customElements.get("hrc-product-card")) {
  class HrcProductCard extends HTMLElement {
    constructor() {
      super();

      this.cardId = this.dataset.cardId || "";
      this.track = this.querySelector(".hrc-featured-img-track");
      this.slides = [];
      this.dots = [];
      this.prevBtn = null;
      this.nextBtn = null;
      this.index = 0;
      this.total = 0;
      this._stackMode = false;
      this._drag = null;
      this._animating = false;
      this._suppressClick = false;

      this._quickAddBtn = this.querySelector(".hrc-featured-quickadd-btn");
      this._productHandle = this.dataset.handle || "";
      this._onQuickAdd = this._onQuickAdd.bind(this);

      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onBoxClick = this._onBoxClick.bind(this);
    }

    connectedCallback() {
      this._initSlider();
      this._initQuickAdd();
    }

    disconnectedCallback() {
      this._destroySlider();
      if (this._quickAddBtn) {
        this._quickAddBtn.removeEventListener("click", this._onQuickAdd);
      }
    }

    _initQuickAdd() {
      if (!this._quickAddBtn) return;
      const form = this.querySelector("form.hrc-featured-quickadd-form");
      if (form) form.addEventListener("submit", (e) => e.preventDefault());
      this._quickAddBtn.addEventListener("click", this._onQuickAdd);
    }

    _onQuickAdd(e) {
      e.preventDefault();
      e.stopPropagation();
      if (this._quickAddBtn?.disabled) return;
      if (this._quickAddBtn?.classList.contains("hrc-featured-quickadd-btn--coming-soon")) {
        return;
      }
      if (this.dataset.comingSoon === "true") {
        return;
      }
      if (this.dataset.giftOnly === "true") {
        return;
      }
      if (window.HrcGift?.isGiftOnlyProduct?.({
        id: this.dataset.productId,
        product_id: this.dataset.productId,
        handle: this._productHandle,
        variant_id: this.dataset.variantId,
      })) {
        return;
      }
      if (!this._productHandle) {
        console.warn("[Product Card] data-handle eksik.");
        return;
      }
      if (typeof window.HrcQuickAddPopup?.open === "function") {
        const compareAt = this.dataset.compareAtPrice;
        window.HrcQuickAddPopup.open(this._productHandle, {
          imageIndex: this.index,
          price: this.dataset.basePrice || this.dataset.price,
          compareAtPrice: compareAt || undefined,
          primaryTag: this.dataset.primaryTag || "",
        });
      } else {
        console.warn("[Product Card] QuickAddPopup yüklenmemiş.");
      }
    }

    _initSlider() {
      if (!this.track) return;
      this.slides = Array.from(
        this.track.querySelectorAll(".hrc-featured-img-slide"),
      );
      this.total = this.slides.length;
      if (this.total <= 1) return;

      this.prevBtn = this.querySelector(".hrc-featured-nav--prev");
      this.nextBtn = this.querySelector(".hrc-featured-nav--next");
      this.dots = Array.from(this.querySelectorAll(".hrc-featured-dot"));

      this._markActiveSlide(0);

      if (this.prevBtn) this.prevBtn.addEventListener("click", this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener("click", this._onNext);

      this.dots.forEach((dot, i) => {
        dot.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._goTo(i);
        });
      });

      this._stackMode = true;
      this.box = this.track.closest(".hrc-featured-img-box");
      this.track.classList.add("is-stacked");
      this._paintStack();
      this.track.addEventListener("pointerdown", this._onPointerDown);
      this.track.addEventListener("pointermove", this._onPointerMove, {
        passive: false,
      });
      this.track.addEventListener("pointerup", this._onPointerUp);
      this.track.addEventListener("pointercancel", this._onPointerUp);
      if (this.box) {
        this.box.addEventListener("click", this._onBoxClick, true);
      }

      this._updateNav();
    }

    _destroySlider() {
      if (this.prevBtn) this.prevBtn.removeEventListener("click", this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener("click", this._onNext);
      if (this.track) {
        this.track.removeEventListener("pointerdown", this._onPointerDown);
        this.track.removeEventListener("pointermove", this._onPointerMove);
        this.track.removeEventListener("pointerup", this._onPointerUp);
        this.track.removeEventListener("pointercancel", this._onPointerUp);
      }
      if (this.box) this.box.removeEventListener("click", this._onBoxClick, true);
    }

    _onPrev(e) {
      e.preventDefault();
      e.stopPropagation();
      this._goTo(this.index - 1);
    }
    _onNext(e) {
      e.preventDefault();
      e.stopPropagation();
      this._goTo(this.index + 1);
    }

    _goTo(index) {
      if (this.total <= 1 || this._animating) return;

      const next = Math.max(0, Math.min(this.total - 1, index));
      if (next === this.index) return;
      this._setUnder(next);
      this._settleWipe(next > this.index ? "right" : "left", 100, next);
    }

    _updateNav() {
      const atStart = this._stackMode && this.index <= 0;
      const atEnd = this._stackMode && this.index >= this.total - 1;
      const prevOff = this._stackMode ? atStart : this.total <= 1;
      const nextOff = this._stackMode ? atEnd : this.total <= 1;

      if (this.prevBtn) {
        this.prevBtn.classList.toggle("is-disabled", prevOff);
        this.prevBtn.setAttribute("aria-disabled", prevOff ? "true" : "false");
      }
      if (this.nextBtn) {
        this.nextBtn.classList.toggle("is-disabled", nextOff);
        this.nextBtn.setAttribute("aria-disabled", nextOff ? "true" : "false");
      }
    }

    _onPointerDown(event) {
      if (this._animating) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest("button")) return;

      this._suppressClick = false;
      this._drag = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        dx: 0,
        axis: "",
        under: -1,
      };
    }

    _onPointerMove(event) {
      const drag = this._drag;
      if (!drag || event.pointerId !== drag.id || this._animating) return;

      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.axis) {
        if (Math.hypot(dx, dy) < 8) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (drag.axis === "y") {
          this._drag = null;
          return;
        }
        this.track.setPointerCapture(event.pointerId);
      }
      if (drag.axis !== "x") return;

      const edge = dx < 0 ? "right" : "left";
      this._setUnder(dx < 0 ? this.index + 1 : this.index - 1);
      drag.under = this._underIndex;
      drag.edge = edge;

      event.preventDefault();
      if (drag.under < 0) return;

      drag.dx = dx;
      if (Math.abs(dx) > 8) this._suppressClick = true;
      const width = this.track.clientWidth || 1;
      this._setWipe(edge, (Math.abs(dx) / width) * 100);
    }

    _onPointerUp(event) {
      const drag = this._drag;
      if (!drag || event.pointerId !== drag.id) return;
      this._drag = null;
      if (drag.axis !== "x") return;

      const width = this.track.clientWidth || 1;
      const commit = Math.abs(drag.dx) > width * 0.2 && drag.under >= 0;
      if (!commit) {
        this._settleWipe(drag.edge, 0, null);
        return;
      }
      this._settleWipe(drag.edge, 100, drag.under);
    }

    _setUnder(index) {
      const under = index >= 0 && index < this.total ? index : -1;
      this._underIndex = under;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle("is-under", i === under);
      });
    }

    _setWipe(edge, percent) {
      const cut = Math.max(0, Math.min(100, percent));
      const clip =
        edge === "right"
          ? `inset(0 ${cut}% 0 0)`
          : edge === "left"
            ? `inset(0 0 0 ${cut}%)`
            : "inset(0 0 0 0)";
      this.slides[this.index].style.clipPath = clip;
    }

    _wipeAmount(slide, edge) {
      const value = slide.style.clipPath || "";
      const match =
        edge === "right"
          ? /inset\(\s*0(?:px)?\s+([\d.]+)%/.exec(value)
          : /inset\(\s*0(?:px)?\s+0(?:px)?\s+0(?:px)?\s+([\d.]+)%/.exec(value);
      return match ? Number(match[1]) : 0;
    }

    _settleWipe(edge, percent, nextIndex) {
      const top = this.slides[this.index];
      const reduce = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const finish = () => {
        if (settled) return;
        settled = true;
        this._animating = false;
        this.track.classList.remove("is-settling");
        top.removeEventListener("transitionend", onEnd);
        if (nextIndex != null) this.index = nextIndex;
        this._paintStack();
      };
      const onEnd = (event) => {
        if (event.target !== top || event.propertyName !== "clip-path") return;
        finish();
      };
      let settled = false;
      const start = this._wipeAmount(top, edge);

      if (reduce || start === percent) {
        finish();
        return;
      }

      this.track.classList.remove("is-settling");
      this._setWipe(edge, start);
      top.getBoundingClientRect();
      this._animating = true;
      this.track.classList.add("is-settling");
      top.addEventListener("transitionend", onEnd);
      window.setTimeout(finish, 520);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!settled) this._setWipe(edge, percent);
        });
      });
    }

    _paintStack() {
      const under = this.index < this.total - 1 ? this.index + 1 : -1;
      this._underIndex = under;
      this.slides.forEach((slide, i) => {
        slide.style.clipPath = "";
        slide.classList.toggle("is-top", i === this.index);
        slide.classList.toggle("is-under", i === under);
        slide.classList.toggle("is-active", i === this.index);
      });
      this.dots.forEach((dot, i) =>
        dot.classList.toggle("is-active", i === this.index),
      );
      this._updateNav();
    }

    _onBoxClick(event) {
      if (!this._suppressClick) return;
      if (event.target.closest("button")) return;
      event.preventDefault();
      event.stopPropagation();
      this._suppressClick = false;
    }

    _markActiveSlide(index) {
      this.slides.forEach((slide, i) =>
        slide.classList.toggle("is-active", i === index),
      );
    }
  }

  customElements.define("hrc-product-card", HrcProductCard);
}
